import {
  useGetUtaFacultyMasterDataQuery,
  useLazyGetAwardeeByIdQuery,
  useLazyGetUTAFacultyByIdQuery,
} from "../../services/rtkQueryServices/checkApiService";
import "./UTAFacultyAwards.scss";
import {
  Box,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import Slider from "../Shared/Widgets/Slider/Slider";
import Awardee from "../Awardee/Awardees";
import Loader from "../Shared/Widgets/Loader/Loader";

const DEFAULT_PROFILE_IMG =
  "https://www.digitalmeasures.com/services/self-service-reporting/profiles/images/default";

const NA_AWARD_NAMES = [
  "Member National Academy of Engineering",
  "Member National Academy of Medicine",
  "Member National Academy of Sciences",
];
const NA_COMBINED_HEADER = "Member National Academy";

const UTAFacultyAwards = () => {
  const { data: utaFacultyMasterData, error } =
    useGetUtaFacultyMasterDataQuery();
  const [setFacultyData, { data: facultyData, isFetching: facultyFetching }] =
    useLazyGetUTAFacultyByIdQuery();
  const [setFacultyDataEng, { data: facultyDataEng }] =
    useLazyGetUTAFacultyByIdQuery();
  const [setFacultyDataMed, { data: facultyDataMed }] =
    useLazyGetUTAFacultyByIdQuery();
  const [setFacultyDataSci, { data: facultyDataSci }] =
    useLazyGetUTAFacultyByIdQuery();
  const [isNACombined, setIsNACombined] = useState(false);
  const [prestigiousAwards, setPrestigiousAwards] = useState([]);
  const [universityAwards, setUniversityAwards] = useState([]);
  const [utaFacultyAwardHeader, setUtaFacultyAwardHeader] = useState();
  const [openSlider, setOpenSlider] = useState(false);
  const [sliderTitle, setSliderTitle] = useState("");
  const [sliderUsername, setSliderUsername] = useState(null);
  const [setAwardeeData, { data: awardeeData }] = useLazyGetAwardeeByIdQuery();
  // Below this width the award lists collapse into a single dropdown so the
  // faculty content isn't buried under a long scroll (matches the 1024px
  // layout flip in the SCSS).
  const isCompact = useMediaQuery("(max-width:1024px)");

  useEffect(() => {
    if (openSlider) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
    };
  }, [openSlider]);

  useEffect(() => {
    let a1 = [];
    let a2 = [];
    utaFacultyMasterData?.data?.forEach((item) => {
      if (item.isPrestigiousAward === 1) a1.push(item);
      else a2.push(item);
    });
    setPrestigiousAwards(a1);
    setUniversityAwards(a2);
  }, [utaFacultyMasterData]);

  const processedPrestigiousAwards = useMemo(() => {
    const result = [];
    let naGroupInserted = false;
    for (const item of prestigiousAwards) {
      if (NA_AWARD_NAMES.includes(item.Award)) {
        if (!naGroupInserted) {
          const naChildren = prestigiousAwards.filter((a) =>
            NA_AWARD_NAMES.includes(a.Award)
          );
          result.push({
            isNAGroup: true,
            Award: NA_COMBINED_HEADER,
            children: naChildren,
          });
          naGroupInserted = true;
        }
      } else {
        result.push(item);
      }
    }
    return result;
  }, [prestigiousAwards]);

  const handleNACombinedClick = (children) => {
    setIsNACombined(true);
    setUtaFacultyAwardHeader(NA_COMBINED_HEADER);
    const eng = children.find(
      (c) => c.Award === "Member National Academy of Engineering"
    );
    const med = children.find(
      (c) => c.Award === "Member National Academy of Medicine"
    );
    const sci = children.find(
      (c) => c.Award === "Member National Academy of Sciences"
    );
    if (eng) setFacultyDataEng(eng.pk_award);
    if (med) setFacultyDataMed(med.pk_award);
    if (sci) setFacultyDataSci(sci.pk_award);
  };

  // Random initial selection - runs after processedPrestigiousAwards is built
  useEffect(() => {
    if (processedPrestigiousAwards.length === 0) return;
    const randomIndex = Math.floor(
      Math.random() * processedPrestigiousAwards.length
    );
    const pick = processedPrestigiousAwards[randomIndex];
    if (pick.isNAGroup) {
      handleNACombinedClick(pick.children);
    } else {
      setIsNACombined(false);
      setFacultyData(pick.pk_award);
      setUtaFacultyAwardHeader(pick.Award);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedPrestigiousAwards]);

  const handleAwardNameClick = (pk_award, awardName) => {
    setIsNACombined(false);
    pk_award && setFacultyData(pk_award);
    setUtaFacultyAwardHeader(awardName);
  };

  const handleAwardeeClick = (data) => {
    data.RaiseProjectID && setAwardeeData(data?.RaiseProjectID);
    setSliderTitle(data?.FullName);
    setSliderUsername(data?.utaUsername || null);
    setOpenSlider(true);
  };

  const handleOnCloseClick = () => {
    setOpenSlider(false);
    document.body.style.overflow = "auto";
  };

  // Count of faculty shown for the current selection (drives the panel subtitle).
  const shownFacultyCount = isNACombined
    ? (facultyDataEng?.data?.length || 0) +
      (facultyDataMed?.data?.length || 0) +
      (facultyDataSci?.data?.length || 0)
    : facultyData?.data?.length || 0;

  // Faculty card: photo overhangs a solid themed card with centered white text.
  const renderFacultyCard = (item, index) => (
    <button
      type="button"
      className="faculty-card"
      key={`${item.RaiseProjectID || item.FullName}-${index}`}
      onClick={() => handleAwardeeClick(item)}
    >
      <div className="faculty-card-photo">
        <img
          src={item.faculty_profile_link || DEFAULT_PROFILE_IMG}
          alt={item.FullName || "Faculty profile"}
          onError={(e) => {
            e.currentTarget.src = DEFAULT_PROFILE_IMG;
          }}
        />
      </div>
      <div className="faculty-card-name">{item.FullName}</div>
      {item.Department && (
        <div className="faculty-card-dept">{item.Department}</div>
      )}
    </button>
  );

  // Dynamic tile sizing: 1 result -> one large tile, a few -> medium, many -> dense.
  const renderFacultyGrid = (rows) => {
    if (!rows?.length)
      return <div className="uta-faculty-empty">No faculty found.</div>;
    const tier = rows.length === 1 ? "solo" : rows.length <= 4 ? "md" : "sm";
    return (
      <div className={`uta-faculty-grid uta-faculty-grid--${tier}`}>
        {rows.map(renderFacultyCard)}
      </div>
    );
  };

  // NA combined view: only show a section (header + grid) when it has faculty.
  const renderNASection = (label, rows) =>
    rows?.length ? (
      <React.Fragment key={label}>
        <div className="uta-na-section">{label}</div>
        {renderFacultyGrid(rows)}
      </React.Fragment>
    ) : null;

  const renderAwardRow = (item) => {
    const active = item.isNAGroup
      ? isNACombined
      : !isNACombined && item.Award === utaFacultyAwardHeader;
    return (
      <div
        key={item.isNAGroup ? "na-group" : item.pk_award}
        className={`uta-award-row${active ? " active" : ""}`}
        onClick={() =>
          item.isNAGroup
            ? handleNACombinedClick(item.children)
            : handleAwardNameClick(item.pk_award, item.Award)
        }
      >
        {item.Award}
      </div>
    );
  };

  // Compact (mobile) award picker: one grouped <Select> standing in for both
  // lists. Value is the award name (unique across both groups).
  const handleAwardSelectChange = (name) => {
    const naGroup = processedPrestigiousAwards.find(
      (i) => i.isNAGroup && i.Award === name
    );
    if (naGroup) {
      handleNACombinedClick(naGroup.children);
      return;
    }
    const match = [...processedPrestigiousAwards, ...universityAwards].find(
      (i) => !i.isNAGroup && i.Award === name
    );
    if (match) handleAwardNameClick(match.pk_award, match.Award);
  };

  const compactAwardPicker = (
    <FormControl fullWidth size="small" className="uta-award-select">
      <InputLabel id="uta-award-select-label">Award</InputLabel>
      <Select
        labelId="uta-award-select-label"
        label="Award"
        value={utaFacultyAwardHeader || ""}
        onChange={(e) => handleAwardSelectChange(e.target.value)}
      >
        <ListSubheader>Selected Prestigious Awards</ListSubheader>
        {processedPrestigiousAwards.map((item) => (
          <MenuItem
            key={item.isNAGroup ? "na-group" : item.pk_award}
            value={item.Award}
          >
            {item.Award}
          </MenuItem>
        ))}
        <ListSubheader>Selected University Awards</ListSubheader>
        {universityAwards.map((item) => (
          <MenuItem key={item.pk_award} value={item.Award}>
            {item.Award}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (error) {
    return <Typography variant="h6">Error: {error.message}</Typography>;
  }

  return (
    <div className="p-3 h-100 overflow-auto uta-landing">
      <Box
        className="uta-hero"
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: "16px",
          p: { xs: 3, md: 4 },
          mb: 3,
          color: "#fff",
          background: (t) =>
            `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
          boxShadow: "0 10px 30px rgba(16,24,40,0.12)",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -60,
            right: -40,
            width: 220,
            height: 220,
            borderRadius: "50%",
            bgcolor: "secondary.main",
            opacity: 0.18,
          }}
        />
        <Box sx={{ position: "relative" }}>
          <Typography
            sx={{ fontWeight: 800, fontSize: { xs: "1.55rem", md: "2rem" }, lineHeight: 1.1 }}
          >
            University of Texas at Arlington
          </Typography>
          <Typography
            variant="overline"
            sx={{ display: "block", mt: 1, color: "secondary.main", fontWeight: 700, letterSpacing: "0.18em" }}
          >
            Awards &amp; Honors
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 640, mt: 1.5 }}>
            Celebrating UTA faculty recognized with the nation&apos;s most
            prestigious academic honors and university awards.
          </Typography>
        </Box>
      </Box>

      <div className="uta-body">
        {isCompact ? (
          <div className="uta-award-select-wrapper">{compactAwardPicker}</div>
        ) : (
          <div className="uta-award-nav">
            <div className="uta-award-group">
              <Typography
                variant="overline"
                className="uta-award-group-title"
                sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: "0.1em" }}
              >
                Selected Prestigious Awards
              </Typography>
              <div className="uta-award-list">
                {processedPrestigiousAwards.map(renderAwardRow)}
              </div>
            </div>
            <div className="uta-award-group">
              <Typography
                variant="overline"
                className="uta-award-group-title"
                sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: "0.1em" }}
              >
                Selected University Awards
              </Typography>
              <div className="uta-award-list">
                {universityAwards.map((item) =>
                  renderAwardRow({ ...item, isNAGroup: false })
                )}
              </div>
            </div>
          </div>
        )}

        <div className="uta-faculty-panel">
          <Typography variant="h5" className="uta-faculty-header" sx={{ fontWeight: 800 }}>
            {utaFacultyAwardHeader}
          </Typography>
          {utaFacultyAwardHeader && (
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              {shownFacultyCount}{" "}
              {shownFacultyCount === 1 ? "faculty member" : "faculty members"}
            </Typography>
          )}

          {facultyFetching && !isNACombined ? (
            <Loader inline />
          ) : isNACombined ? (
            <>
              {renderNASection("Engineering", facultyDataEng?.data)}
              {renderNASection("Medicine", facultyDataMed?.data)}
              {renderNASection("Sciences", facultyDataSci?.data)}
            </>
          ) : (
            renderFacultyGrid(facultyData?.data)
          )}
        </div>
      </div>

      <Slider isOpen={openSlider} onClose={handleOnCloseClick}>
        <Awardee
          awardeeData={awardeeData}
          handleOnCloseClick={handleOnCloseClick}
          sliderTitle={sliderTitle}
          utaUsername={sliderUsername}
        />
      </Slider>
    </div>
  );
};
export default UTAFacultyAwards;
